import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import axios from "axios";

import { API_ERRO_TYPE_CANCEL } from "../../config/general";
import {
	companiesService,
	departmentsService,
	employeesService,
} from "../../redux/services";
import { buildMenuItems } from "../Sidebar/menuItems";

import "./index.css";

const DEBOUNCE_MS = 350;
const MIN_CHARS = 2;
const MAX_POR_GRUPO = 5;
const COMPACTO_MQ = "(max-width: 767px)";


const FONTES = [
	{
		key: "funcionarios",
		label: "Funcionários",
		icon: "fad fa-user",
		permission: "funcionarios.list",
		to: "/cadastro/employees",
		service: employeesService,
	},
	{
		key: "empresas",
		label: "Empresas",
		icon: "fad fa-building",
		permission: "empresas.list",
		to: "/list/companies",
		service: companiesService,
	},
	{
		key: "departamentos",
		label: "Departamentos",
		icon: "fad fa-sitemap",
		permission: "departamentos.list",
		to: "/cadastro/departments",
		service: departmentsService,
	},
];

const rotuloDoRegistro = (item) => {
	if (item?.nome) return item.nome;
	if (item?.descricao) return item.descricao;

	const equipamento = [item?.marca, item?.modelo].filter(Boolean).join(" ");
	if (equipamento) return equipamento;

	return "—";
};

const normalizar = (valor) =>
	String(valor ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

const achatarMenu = (items, trilha = []) =>
	items.reduce((acc, item) => {
		if (item.children) return acc.concat(achatarMenu(item.children, [...trilha, item.label]));
		if (!item.to) return acc;

		return acc.concat({
			key: item.key,
			label: item.label,
			to: item.to,
			trilha: trilha.join(" › "),
		});
	}, []);

function GlobalSearch({ permissions = [], history }) {
	const [termo, setTermo] = useState("");
	const [aberto, setAberto] = useState(false);
	const [carregando, setCarregando] = useState(false);
	const [grupos, setGrupos] = useState([]);
	const [ativo, setAtivo] = useState(0);

	const [compacto, setCompacto] = useState(
		() => typeof window !== "undefined" && window.matchMedia(COMPACTO_MQ).matches
	);
	const [expandido, setExpandido] = useState(false);

	const inputRef = useRef(null);
	const containerRef = useRef(null);
	const cancelRef = useRef(null);
	const debounceRef = useRef(null);

	const isMac = useMemo(
		() => typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform),
		[]
	);

	const fontesPermitidas = useMemo(
		() => FONTES.filter((f) => permissions.includes(f.permission)),
		[permissions]
	);

	const telas = useMemo(() => achatarMenu(buildMenuItems(permissions)), [permissions]);

	// --- Busca ---------------------------------------------------------------
	const buscar = useCallback(async (texto) => {
		if (cancelRef.current) cancelRef.current.cancel();
		cancelRef.current = axios.CancelToken.source();
		const cancelToken = cancelRef.current.token;

		setCarregando(true);

		const alvo = normalizar(texto);

		// Telas: filtro local, sem requisição
		const telasEncontradas = telas
			.filter((t) => normalizar(`${t.label} ${t.trilha}`).includes(alvo))
			.slice(0, MAX_POR_GRUPO)
			.map((t) => ({ id: t.key, titulo: t.label, contexto: t.trilha, to: t.to }));

		const respostas = await Promise.all(
			fontesPermitidas.map(async (fonte) => {
				try {
					const resp = await fonte.service.getAutocomplete({ search: texto, cancelToken });
					const itens = resp?.data?.data ?? resp?.data ?? [];

					return {
						fonte,
						itens: (Array.isArray(itens) ? itens : []).slice(0, MAX_POR_GRUPO),
					};
				} catch (e) {
					// Cancelamento é fluxo normal (o usuário continuou digitando)
					if (e?.error_type === API_ERRO_TYPE_CANCEL || axios.isCancel(e)) return null;
					console.error(`Erro ao buscar ${fonte.key}`, e);
					return { fonte, itens: [] };
				}
			})
		);

		// Alguma requisição foi cancelada: uma busca mais nova assumiu
		if (respostas.some((r) => r === null)) return;

		const gruposCadastro = respostas
			.filter((r) => r.itens.length > 0)
			.map(({ fonte, itens }) => ({
				key: fonte.key,
				label: fonte.label,
				icon: fonte.icon,
				resultados: itens.map((item) => {
					const titulo = rotuloDoRegistro(item);

					return {
						id: `${fonte.key}-${item.uuid ?? item.id}`,
						titulo,
						contexto: fonte.label,
						to: `${fonte.to}?search=${encodeURIComponent(titulo)}`,
					};
				}),
			}));

		const proximos = [...gruposCadastro];

		if (telasEncontradas.length) {
			proximos.push({
				key: "telas",
				label: "Telas",
				icon: "fad fa-window-maximize",
				resultados: telasEncontradas,
			});
		}

		setGrupos(proximos);
		setAtivo(0);
		setCarregando(false);
	}, [fontesPermitidas, telas]);

	// Dispara a busca com debounce sempre que o termo muda
	useEffect(() => {
		if (debounceRef.current) clearTimeout(debounceRef.current);

		if (normalizar(termo).length < MIN_CHARS) {
			if (cancelRef.current) cancelRef.current.cancel();
			setGrupos([]);
			setCarregando(false);
			return;
		}

		debounceRef.current = setTimeout(() => buscar(termo), DEBOUNCE_MS);

		return () => clearTimeout(debounceRef.current);
	}, [termo, buscar]);

	// Limpa requisição/timer pendentes ao desmontar
	useEffect(() => () => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		if (cancelRef.current) cancelRef.current.cancel();
	}, []);

	// --- Interação -----------------------------------------------------------
	const planos = useMemo(
		() => grupos.reduce((acc, g) => acc.concat(g.resultados), []),
		[grupos]
	);

	useEffect(() => {
		const mq = window.matchMedia(COMPACTO_MQ);
		const onChange = (e) => setCompacto(e.matches);

		setCompacto(mq.matches);

		if (mq.addEventListener) mq.addEventListener("change", onChange);
		else mq.addListener(onChange);

		return () => {
			if (mq.removeEventListener) mq.removeEventListener("change", onChange);
			else mq.removeListener(onChange);
		};
	}, []);

	const fechar = useCallback(() => {
		setAberto(false);
		setAtivo(0);
	}, []);

	const recolher = useCallback(() => {
		setExpandido(false);
		setTermo("");
		setGrupos([]);
		fechar();
	}, [fechar]);

	const expandir = useCallback(() => {
		setExpandido(true);
		setTimeout(() => inputRef.current?.focus(), 0);
	}, []);

	const abrirResultado = useCallback((resultado) => {
		if (!resultado) return;
		setTermo("");
		setGrupos([]);
		setExpandido(false);
		fechar();
		inputRef.current?.blur();
		history.push(resultado.to);
	}, [fechar, history]);

	useEffect(() => {
		const onKeyDown = (e) => {
			if (!(e.ctrlKey || e.metaKey) || e.key?.toLowerCase() !== "k") return;
			e.preventDefault();

			if (compacto && !expandido) expandir();
			else inputRef.current?.focus();
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [compacto, expandido, expandir]);

	useEffect(() => {
		if (!aberto && !expandido) return;

		const onDown = (e) => {
			if (containerRef.current?.contains(e.target)) return;
			if (compacto) recolher();
			else fechar();
		};

		document.addEventListener("mousedown", onDown);
		return () => document.removeEventListener("mousedown", onDown);
	}, [aberto, expandido, compacto, fechar, recolher]);

	const onKeyDown = (e) => {
		if (e.key === "Escape") {
			inputRef.current?.blur();
			if (compacto) recolher();
			else fechar();
			return;
		}

		if (!planos.length) return;

		if (e.key === "ArrowDown") {
			e.preventDefault();
			setAberto(true);
			setAtivo((i) => (i + 1) % planos.length);
			return;
		}

		if (e.key === "ArrowUp") {
			e.preventDefault();
			setAberto(true);
			setAtivo((i) => (i - 1 + planos.length) % planos.length);
			return;
		}

		if (e.key === "Enter") {
			e.preventDefault();
			abrirResultado(planos[ativo]);
		}
	};

	const mostraCampo = !compacto || expandido;
	const mostraPainel = mostraCampo && aberto && normalizar(termo).length >= MIN_CHARS;
	let indice = -1;

	if (!mostraCampo) {
		return (
			<div className="byp-search byp-search--compacto" ref={containerRef}>
				<button
					type="button"
					className="byp-search__toggle"
					aria-label="Buscar"
					aria-expanded={false}
					onClick={expandir}
				>
					<i className="far fa-search" />
				</button>
			</div>
		);
	}

	return (
		<div
			className={`byp-search ${compacto ? "byp-search--compacto is-expanded" : ""}`}
			ref={containerRef}
		>
			<div className="byp-search__field">
				<i className="far fa-search" />
				<input
					ref={inputRef}
					type="text"
					autoComplete="off"
					aria-label="Buscar"
					role="combobox"
					aria-expanded={mostraPainel}
					aria-controls="byp-search-panel"
					aria-autocomplete="list"
					placeholder={compacto ? "Buscar..." : "Buscar funcionário, empresa, tela..."}
					value={termo}
					onChange={(e) => {
						setTermo(e.target.value);
						setAberto(true);
					}}
					onFocus={() => setAberto(true)}
					onKeyDown={onKeyDown}
				/>

				{carregando && <i className="fad fa-spinner-third fa-spin byp-search__spinner" />}

				{(compacto || termo) ? (
					<button
						type="button"
						className="byp-search__clear"
						aria-label={compacto ? "Fechar busca" : "Limpar busca"}
						onClick={() => {
							if (compacto) return recolher();
							setTermo("");
							setGrupos([]);
							inputRef.current?.focus();
						}}
					>
						<i className="far fa-times" />
					</button>
				) : (
					!carregando && <kbd>{isMac ? "⌘ K" : "Ctrl + K"}</kbd>
				)}
			</div>

			{mostraPainel && (
				<div className="byp-search__panel" id="byp-search-panel" role="listbox">
					{grupos.length === 0 ? (
						<p className="byp-search__empty">
							{carregando ? "Buscando…" : `Nada encontrado para "${termo}"`}
						</p>
					) : (
						grupos.map((grupo) => (
							<div key={grupo.key} className="byp-search__group">
								<div className="byp-search__group-label">
									<i className={grupo.icon} /> {grupo.label}
								</div>
								{grupo.resultados.map((resultado) => {
									indice += 1;
									const atual = indice;

									return (
										<button
											key={resultado.id}
											type="button"
											role="option"
											aria-selected={atual === ativo}
											className={`byp-search__item ${atual === ativo ? "is-active" : ""}`}
											onMouseEnter={() => setAtivo(atual)}
											onClick={() => abrirResultado(resultado)}
										>
											<span className="byp-search__item-title">{resultado.titulo}</span>
											{resultado.contexto && (
												<span className="byp-search__item-context">{resultado.contexto}</span>
											)}
										</button>
									);
								})}
							</div>
						))
					)}
				</div>
			)}
		</div>
	);
}

const mapStateToProps = (state) => {
	return {
		permissions: state.auth.userData.permissions,
	};
};

export default connect(mapStateToProps)(withRouter(GlobalSearch));
